import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BarChartIcon from "@mui/icons-material/BarChart";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import ForumIcon from "@mui/icons-material/Forum";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TradeExecutionWidget from "../../components/widgets/TradeExecutionWidget";

const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';

const quickActions = [
  { title: "Create Strategy", icon: <AutoGraphIcon />, link: "/user/strategy-builder" },
  { title: "Trading History", icon: <HistoryIcon />, link: "/user/history" },
  { title: "Analytics", icon: <BarChartIcon />, link: "/user/technical" },
  { title: "Account Settings", icon: <SettingsIcon />, link: "/user/settings" },
];

function UserDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [loadingSub, setLoadingSub] = useState(true);
  const [loadingStrategies, setLoadingStrategies] = useState(true);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: "", description: "" });
  const [showPromotion, setShowPromotion] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await fetch(`${STRATEGY_ENGINE_URL}/api/plans/my-subscription`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (data.success) {
          setSubscription(data.data.subscription);
          setUsage(data.data.usage);
        }
      } catch (err) {
        console.error('[Dashboard] Failed to fetch subscription:', err);
      } finally {
        setLoadingSub(false);
      }
    };

    const fetchStrategies = async () => {
      try {
        const res = await fetch(`${STRATEGY_ENGINE_URL}/api/strategies`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (data.success && data.data) {
          const strats = Array.isArray(data.data) ? data.data : (data.data.strategies || []);
          setStrategies(strats.slice(0, 5));
        }
      } catch (err) {
        console.error('[Dashboard] Failed to fetch strategies:', err);
      } finally {
        setLoadingStrategies(false);
      }
    };

    fetchSubscription();
    fetchStrategies();
  }, []);

  const handleTicketSubmit = () => {
    setOpenTicketDialog(false);
    alert("Support ticket submitted successfully! Our team will respond soon.");
    setNewTicket({ subject: "", description: "" });
  };

  const handleTicketChange = (e) => {
    const { name, value } = e.target;
    setNewTicket({ ...newTicket, [name]: value });
  };

  const planName = subscription?.planName || user?.planId || 'Starter';
  const isEnterprise = subscription?.planId === 'enterprise';
  const strategyUsagePct = usage ? Math.min(100, Math.round((usage.strategies.current / usage.strategies.limit) * 100)) : 0;

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Welcome Header */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Box sx={{
            display: "flex", alignItems: "center", p: 2, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <Avatar
              sx={{ width: 56, height: 56, mr: 2, border: '2px solid rgba(0,255,148,0.3)', bgcolor: '#1a5c3a' }}
            >
              {(user?.displayName || user?.email || '?')[0].toUpperCase()}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
                Welcome back, {user?.displayName || user?.email?.split('@')[0] || 'Trader'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                Here's what's happening with your trading account today
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
          <Button variant="contained" startIcon={<AutoGraphIcon />} onClick={() => navigate("/user/strategy-builder")} sx={{ mr: 2 }}>
            New Strategy
          </Button>
          <Button variant="outlined" startIcon={<ForumIcon />} onClick={() => setOpenTicketDialog(true)}>
            Get Support
          </Button>
        </Grid>
      </Grid>

      {/* Promotion Card */}
      {showPromotion && !isEnterprise && (
        <Paper sx={{ p: 2, mb: 4, background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)", color: "white", position: "relative" }}>
          <IconButton sx={{ position: "absolute", top: 8, right: 8, color: "white" }} onClick={() => setShowPromotion(false)}>
            <CloseIcon />
          </IconButton>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6">Upgrade to Enterprise Plan</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get access to advanced AI features, priority support, and unlimited strategies. Boost your trading potential with our most powerful tools.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Button variant="contained" color="secondary" onClick={() => navigate("/user/plans")}>
                View Enterprise Features
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Account Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>Current Plan</Typography>
            {loadingSub ? <CircularProgress size={20} /> : (
              <>
                <Chip label={planName} color="primary" sx={{ mb: 2 }} />
                {subscription?.currentPeriodEnd && (
                  <Typography variant="body2">
                    <strong>Renews:</strong> {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </Typography>
                )}
                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" gutterBottom>Strategy Usage</Typography>
                  <LinearProgress variant="determinate" value={strategyUsagePct} sx={{ height: 8, borderRadius: 4 }} />
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    {usage?.strategies.current || 0} / {usage?.strategies.limit || 0} strategies
                  </Typography>
                </Box>
                <Button variant="outlined" fullWidth onClick={() => navigate("/user/plans")} startIcon={<CreditCardIcon />} sx={{ mt: 1 }}>
                  Manage Subscription
                </Button>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>Active Runs</Typography>
            {loadingSub ? <CircularProgress size={20} /> : (
              <>
                <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                  <Typography variant="h4" color="primary">{usage?.activeRuns.current || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>/ {usage?.activeRuns.limit || 0} limit</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usage ? Math.min(100, Math.round((usage.activeRuns.current / usage.activeRuns.limit) * 100)) : 0}
                  sx={{ height: 6, borderRadius: 3, mb: 1 }}
                />
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">Exchanges: {usage?.exchanges.current || 0} / {usage?.exchanges.limit || 0}</Typography>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>Strategies</Typography>
            {loadingSub ? <CircularProgress size={20} /> : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="h4" color="primary">{usage?.strategies.current || 0}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>/ {usage?.strategies.limit || 0} total</Typography>
                </Box>
                <Button variant="contained" size="small" onClick={() => navigate("/user/strategy-builder")} sx={{ mr: 1 }}>Create New</Button>
                <Button variant="outlined" size="small" onClick={() => navigate("/user/strategy-builder")}>View All</Button>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <List dense>
              {quickActions.map((action, index) => (
                <ListItem disablePadding key={index}>
                  <ListItemButton onClick={() => navigate(action.link)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
                    <ListItemText primary={action.title} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Strategies */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Recent Strategies</Typography>
            {loadingStrategies ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : strategies.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>No strategies yet. Create your first one!</Typography>
            ) : (
              <Grid container spacing={2}>
                {strategies.map((strategy) => (
                  <Grid item xs={12} sm={6} md={4} key={strategy.id}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="subtitle2" noWrap sx={{ maxWidth: '70%' }}>{strategy.name}</Typography>
                          <Chip label={strategy.status || 'draft'} size="small" color={strategy.status === 'active' ? 'success' : 'default'} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {strategy.updated_at ? `Updated ${new Date(strategy.updated_at).toLocaleDateString()}` : ''}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: "flex-end", p: 1 }}>
                        <Button size="small" onClick={() => navigate(`/user/strategy-builder`)}>Open</Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="text" endIcon={<AutoGraphIcon />} onClick={() => navigate("/user/strategy-builder")}>
                View All Strategies
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Trade Execution Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TradeExecutionWidget />
        </Grid>
      </Grid>

      {/* Support Ticket Dialog */}
      <Dialog open={openTicketDialog} onClose={() => setOpenTicketDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Create Support Ticket</Typography>
            <IconButton onClick={() => setOpenTicketDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>Our support team is here to help you with any questions or issues.</Typography>
          <TextField autoFocus margin="dense" name="subject" label="Subject" type="text" fullWidth variant="outlined" value={newTicket.subject} onChange={handleTicketChange} sx={{ mb: 2 }} />
          <TextField margin="dense" name="description" label="Description" multiline rows={4} fullWidth variant="outlined" value={newTicket.description} onChange={handleTicketChange} placeholder="Please describe your issue in detail..." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenTicketDialog(false)}>Cancel</Button>
          <Button onClick={handleTicketSubmit} variant="contained" disabled={!newTicket.subject || !newTicket.description}>Submit Ticket</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserDashboard;
